import type { AccountSetUpState } from './types';

/** Where the mailed link lands. One place, so the router and the tests read the same string. */
export const ACCOUNT_SET_UP_PATH = '/app/account/set-up';

/** The query parameter the mail's URL carries — ADR 042's `<rowId>.<secret>` token. */
export const ACCOUNT_SET_UP_TOKEN_PARAM = 'token';

/**
 * The copy, in one place.
 *
 * This screen's whole audience is people who did **not** ask for it: an organizer typed their
 * address into a form at a door, and the first they heard of H2HSpark was an email. Every sentence
 * below is written against that, and the two temptations it creates.
 *
 * **Do not add urgency.** No countdown, no "act now", nothing that implies a place is at risk.
 * `USR-013` AC-9 is that never clicking is a supported outcome — the seat, the ticket and the door
 * token persist indefinitely and the person can walk in and be scanned with no account at all — so
 * a screen that hurried them would be the platform lying about their own seat.
 *
 * **Do not ask for an address anywhere on this page.** The dead-link states offer a fresh link
 * from the **token they are already holding**, never from a form. A public form keyed on an
 * address would be an enumeration oracle on exactly the population this story exists to protect,
 * and `security-invariants#account-enumeration` is explicit that where behaviour must differ it
 * differs in what is emailed, never in what is returned.
 */
export const ACCOUNT_SET_UP_COPY = {
  title: 'Set up your H2HSpark account',
  checking: 'Checking your link…',
  /**
   * What a usable link says before it asks for anything.
   *
   * It leads with the place already being held, because that is the fact the reader can check
   * against their own memory of the evening, and because it is what makes the password field
   * legible rather than alarming.
   */
  intro:
    'Somebody held a place for you at an event. Choose a password and this account is yours — ' +
    'with your ticket, your registrations and the ability to change your plans yourself.',
  emailLabel: 'Your email address',
  passwordLabel: 'Choose a password',
  displayNameLabel: 'Your name',
  displayNameHint: 'This is the name that was entered for you. Change it if it is not right.',
  submit: 'Set up my account',
  submitting: 'Setting up your account…',
  /** After a successful set-up. There is no session — see the route — so this is a sign-in door. */
  done: 'Your account is ready. Sign in with the password you just chose.',
  signIn: 'Go to sign in',
  /** The expired page's one action. It spends the token it already has and never an address. */
  requestAnother: 'Send me a new link',
  requestingAnother: 'Sending…',
} as const;

/**
 * What each dead state says, and what — if anything — it offers.
 *
 * A total record over {@link AccountSetUpState} so a fourth state is a compile error rather than a
 * page that renders nothing. `usable` is here for that totality and is never shown as a dead end.
 */
export const ACCOUNT_SET_UP_DEAD_END_COPY: Readonly<Record<AccountSetUpState, string>> = {
  usable: '',
  /**
   * Expired, and the sentence says what it cost — which is nothing.
   *
   * The second half is `USR-013` AC-9 said to the one person most likely to fear otherwise: a
   * fourteen-day link that ran out is a click to replace, and the place it was about is still
   * held. That is what makes the expiry cheap enough to have at all.
   */
  expired:
    'This link has expired. Your place is still held — nothing was lost. Send yourself a new ' +
    'link and finish setting up whenever you like.',
  /**
   * Used, unknown, malformed and wrong-secret, all in one sentence.
   *
   * It cannot offer a fresh link: for a genuinely used link the account already exists and
   * mailing a live credential to whoever kept the old message would be handing out access to it,
   * and for an unrecognised token there is no row to mail anything to. So the two routes it names
   * are the two that are safe — sign in, or sign up again with the same address, which mails a
   * fresh set-up link to that address and to nobody else (`USR-013` AC-6). It sent people to the
   * organizer until Sumit dropped the organizer's re-send on 2026-10-29 (AC-8 struck, #998).
   */
  used:
    'This link has already been used, or is no longer valid. If you have already set your ' +
    'password, sign in. Otherwise, sign up again with the same email address and we will send ' +
    'you a new link.',
};

/**
 * The **Account claimed** confirmation's copy (`USR-009` AC-3).
 *
 * Separate from {@link ACCOUNT_SET_UP_COPY} because the two are one screen apart and are not the
 * same claim, which is the distinction `USR-013`'s *Out of scope* draws and this is the other half
 * of. The set-up page says *your account is ready* — a sentence about a **credential**. This says
 * *a registration somebody made for you is now yours* — a sentence about a **place at an event**.
 * A member who set a password and then signed in reads both, in that order, and they are both
 * true.
 *
 * **It offers no ticket.** `USR-009` AC-1 withholds access from an unclaimed registration, and
 * this is the moment that withholding ends — which makes it the most tempting place in the product
 * to render a QR code. The ticket is where it has always been, on the registration itself; the
 * last sentence says so rather than reproducing it.
 */
export const ACCOUNT_CLAIMED_COPY = {
  title: 'Account claimed',
  /** One registration. The common case: an organizer added somebody to one night. */
  introOne: 'A registration an organizer created for you is now yours.',
  /** More than one, which happens to somebody added to a series before they ever signed in. */
  introMany: (count: number): string =>
    `${String(count)} registrations an organizer created for you are now yours.`,
  confirmationLabel: 'Confirmation',
  whereTheTicketIs:
    'Your ticket and everything else you hold are on the event itself, under Discover.',
  dismiss: 'Got it',
} as const;
